import Axios from 'axios';
import { useMutation, useQueryClient } from 'react-query';

import apiUrls from 'base/api/urls';
import { Podcast, Tags } from 'base/api/types';
import { StandardError } from 'base/api/errors';
import { useDetailsQuery, useListQuery } from 'base/api/hooks';
import { CommonVars, PK } from 'common/types';
import { STATUSES } from 'common/consts';
import { composeUrl } from 'common/utils';

export const usePodcastList = () => {
  return useListQuery<Podcast>(apiUrls.PODCASTS.LIST);
};

export const usePodcastDetails = (pk?: PK) => {
  return useDetailsQuery<Podcast>({
    queryKey: [apiUrls.PODCASTS.DETAILS, { pk }],
    pk,
  });
};

interface MeditationCreateVars {
  title: string;
  description: string;
  media_file_pk: PK;
  content_goal_pks: PK[];
  is_featured: boolean;
  is_inspiration: boolean;
}

export const usePodcastCreate = () => {
  const queryClient = useQueryClient();
  return useMutation<Podcast, StandardError, MeditationCreateVars>(
    (data) => Axios.post(apiUrls.PODCASTS.LIST, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(apiUrls.PODCASTS.LIST);
        queryClient.invalidateQueries(apiUrls.PODCASTS.LIST);
      },
    },
  );
};

interface MeditationUpdateVars extends MeditationCreateVars, CommonVars {
  status: STATUSES;
}

export const usePodcastUpdate = () => {
  const queryClient = useQueryClient();
  return useMutation<Podcast, StandardError, MeditationUpdateVars>(
    ({ pk, ...data }) =>
      Axios.patch(composeUrl(apiUrls.PODCASTS.DETAILS, { params: { pk } }), data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(apiUrls.PODCASTS.LIST);
        queryClient.setQueryData([apiUrls.PODCASTS.DETAILS, { pk: data.pk }], data);
      },
    },
  );
};

export const usePodcastsTags = (pk?: PK) => {
  return useDetailsQuery<Tags>({ pk, queryKey: [apiUrls.PODCASTS.TAGS, { pk }] });
};

interface MeditationsTagsUpdateVars extends CommonVars {
  payload: Tags;
}

export const usePodcastsTagsUpdate = () => {
  const queryClient = useQueryClient();
  return useMutation<unknown, StandardError, MeditationsTagsUpdateVars>(
    ({ pk, ...payload }) =>
      Axios.put(composeUrl(apiUrls.PODCASTS.TAGS, { params: { pk } }), payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(apiUrls.PODCASTS.TAGS);
      },
    },
  );
};
